import React, { useState, useEffect, useRef } from 'react';
import {
  Wallet, Activity, TrendingUp, CalendarClock, Copy, Users,
} from 'lucide-react';
import api from '../../services/api';
import { getSocket } from '../../services/socket';
import { useAccounts } from '../../context/AccountContext';
import PendingOrdersTable from '../../components/PendingOrdersTable';
import KPITile from '../../components/KPITile';

// An execution's own `profit` is REALIZED P&L, stamped at close, so on an open
// position it is unset — this table used to render "—" for every row and colour
// all of them as losses. The floating figure lives in `live`, read from the
// copier's own terminal; null there means we genuinely could not read it (their
// MT5 is down, or the position just closed), which is still "—" and never a 0.
const livePnl = e => (e?.live && e.live.profit != null ? e.live.profit : null);

const plain = n => `$${Number(n || 0).toFixed(2)}`;
const money = n => `${n >= 0 ? '+' : '−'}$${Math.abs(Number(n || 0)).toFixed(2)}`;
const intentOf = n => (n > 0 ? 'profit' : n < 0 ? 'loss' : 'neutral');
const trendOf  = n => (n > 0 ? 'up' : n < 0 ? 'down' : 'flat');

export default function CopierOverview() {
  const { scopeParams, selectedId } = useAccounts();
  const [dash, setDash]       = useState(null);
  const [open, setOpen]       = useState([]);
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);
  // account:update ticks every ~5s PER connected account. Merging one
  // account's balance into a combined view would show that account's figures
  // as the portfolio's, so an unscoped view refetches instead — throttled, so
  // N accounts still cost at most one request per window.
  const lastDashFetch = useRef(0);

  const params = { params: scopeParams };
  function reloadOpen()    { api.get('/copier/trades/open', params).then(r => setOpen(r.data)).catch(() => {}); }
  function reloadPending() { api.get('/copier/orders/pending', params).then(r => setPending(r.data)).catch(() => {}); }

  useEffect(() => {
    const req = { params: scopeParams };
    setLoading(true);
    Promise.all([
      api.get('/copier/dashboard', req),
      api.get('/copier/trades/open', req),
      api.get('/copier/orders/pending', req),
    ]).then(([d, o, p]) => {
      setDash(d.data);
      setOpen(o.data);
      setPending(p.data);
      lastDashFetch.current = Date.now();
    }).catch(() => {}).finally(() => setLoading(false));

    const sock = getSocket();
    if (!sock) return undefined;
    const reloadDash = () => {
      if (Date.now() - lastDashFetch.current < 10000) return;
      lastDashFetch.current = Date.now();
      api.get('/copier/dashboard', req).then(r => setDash(r.data)).catch(() => {});
    };
    const onAccount = (d) => {
      if (selectedId) {
        // Scoped: the tick either IS this account or belongs to another one.
        if (d.accountId && d.accountId !== selectedId) return;
        setDash(prev => prev ? { ...prev, balance: d.balance, equity: d.equity } : prev);
        return;
      }
      reloadDash();
    };
    const reloadBoth = () => { reloadOpen(); reloadPending(); };
    const handlers = [
      ['account:update',  onAccount],
      ['copy:executed',   reloadBoth],
      ['copy:closed',     reloadBoth],
      ['order:placed',    reloadPending],
      ['order:modified',  reloadPending],
      ['order:cancelled', reloadPending],
      ['order:triggered', reloadBoth],
    ];
    handlers.forEach(([e, fn]) => sock.on(e, fn));
    return () => handlers.forEach(([e, fn]) => sock.off(e, fn));
  }, [scopeParams, selectedId]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) return <p style={{ color: 'var(--text-secondary)' }}>Loading dashboard…</p>;

  const openPnl  = dash?.openPnl  ?? 0;
  const todayPnl = dash?.todayPnl ?? 0;
  // false when any open position had no fresh reading — say so rather than
  // presenting a part-known figure as current.
  const openPnlSub = dash?.openPnlLive === false
    ? 'Floating — some readings delayed'
    : openPnl >= 0 ? 'Floating profit' : 'Floating loss';

  return (
    <>
      <div className="ct-page-header">
        <div>
          <span className="eyebrow">Copier</span>
          <h1>Overview</h1>
          <div className="subtitle">Your account and the positions mirrored from your traders</div>
        </div>
      </div>

      <div className="row">
        <Tile><KPITile label="Balance" value={plain(dash?.balance)} sub="Account balance"
          icon={<Wallet size={16} />} intent="neutral" /></Tile>
        <Tile><KPITile label="Equity" value={plain(dash?.equity)} sub="Incl. floating P&L"
          icon={<Activity size={16} />} intent="neutral" /></Tile>
        <Tile><KPITile label="Open P&L" value={money(openPnl)} sub={openPnlSub}
          icon={<TrendingUp size={16} />} intent={intentOf(openPnl)} trend={trendOf(openPnl)} /></Tile>
        <Tile><KPITile label="Today P&L" value={money(todayPnl)} sub={todayPnl >= 0 ? 'In profit' : 'In loss'}
          icon={<CalendarClock size={16} />} intent={intentOf(todayPnl)} trend={trendOf(todayPnl)} /></Tile>
        <Tile><KPITile label="Copied Trades" value={dash?.totalCopiedTrades ?? 0} sub="All-time executions"
          icon={<Copy size={16} />} intent="neutral" /></Tile>
        <Tile><KPITile label="Active Traders" value={dash?.activeRelationships ?? 0} sub="Currently following"
          icon={<Users size={16} />} intent="neutral" /></Tile>
      </div>

      <div className="row">
        <div className="col-12 mb-4">
          <div className="card">
            <div className="card-header">
              <h5 className="card-title">Pending Orders</h5>
              <div className="card-category" style={{ marginTop: 4 }}>{pending.length} waiting to trigger</div>
            </div>
            <div className="card-body">
              <PendingOrdersTable orders={pending} showTrader />
            </div>
          </div>
        </div>
      </div>

      <div className="row">
        <div className="col-12 mb-4">
          <div className="card">
            <div className="card-header">
              <h5 className="card-title">Open Copied Positions</h5>
              <div className="card-category" style={{ marginTop: 4 }}>{open.length} live</div>
            </div>
            <div className="card-body">
              {open.length === 0 ? (
                <p className="text-muted" style={{ margin: 0 }}>No open copied positions yet.</p>
              ) : (
                <div className="table-responsive table-cards table-cards-wrap">
                  <table className="table">
                    <thead>
                      <tr>
                        <th>Trader</th><th>Symbol</th><th>Type</th>
                        <th className="num">Orig Lots</th><th className="num">Copied Lots</th>
                        <th className="num">Open Price</th><th className="num">P&L</th><th>Since</th>
                      </tr>
                    </thead>
                    <tbody>
                      {open.map(e => {
                        const pnl = livePnl(e);
                        // The copier's broker may name the instrument differently
                        // from the trader's (BTCUSDc vs BTCUSD). Their own name is
                        // what they will see in MT5, so lead with it.
                        const traderSym = e.originalTradeId?.symbol;
                        const ownSym = e.live?.symbol || traderSym;
                        return (
                        <tr key={e._id} className={pnl == null ? '' : pnl >= 0 ? 'row-profit' : 'row-loss'}>
                          <td data-label="Trader">{e.traderId?.username || e.traderId?.email || '—'}</td>
                          <td data-label="Symbol">
                            <strong>{ownSym}</strong>
                            {ownSym !== traderSym && (
                              <div className="text-muted small">trader: {traderSym}</div>
                            )}
                          </td>
                          <td data-label="Type">
                            <span className={`badge badge-${e.originalTradeId?.type === 'buy' ? 'success' : 'danger'}`}>
                              {e.originalTradeId?.type?.toUpperCase()}
                            </span>
                          </td>
                          <td data-label="Orig Lots" className="num">{e.originalTradeId?.lots}</td>
                          <td data-label="Copied Lots" className="num">{e.live?.lots ?? e.scaledLots}</td>
                          <td data-label="Open Price" className="num">{e.originalTradeId?.openPrice?.toFixed(5) || '—'}</td>
                          <td
                            data-label="P&L"
                            className={`num ${pnl == null ? 'text-muted' : pnl >= 0 ? 'text-success' : 'text-danger'}`}
                            title={pnl == null
                              ? 'No live reading — this account\'s MT5 terminal is not reporting right now'
                              : e.live.status === 'stale'
                                ? (e.live.asOf
                                    ? `Last reading ${new Date(e.live.asOf).toLocaleTimeString()}`
                                    : 'Reading age unknown')
                                : undefined}
                          >
                            {pnl == null ? '—' : `${pnl >= 0 ? '+' : ''}${pnl.toFixed(2)}`}
                            {pnl != null && e.live.status === 'stale' && (
                              <span className="text-muted small"> ·delayed</span>
                            )}
                          </td>
                          <td data-label="Since" className="text-muted small">
                            {e.originalTradeId?.openTime ? new Date(e.originalTradeId.openTime).toLocaleString() : '—'}
                          </td>
                        </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

const Tile = ({ children }) => <div className="col-lg-4 col-md-6 col-6 mb-4">{children}</div>;
